import Day from '@awesomecomponents/day-js';
import { Card, H1 } from '@manulife/mux';
import { useMemo } from 'react';
import { Route } from 'react-router-dom';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useAdobeAnalyticsPage from '../../CustomHooks/useAdobeAnalyticsPage';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import Layout from '../Layout/Layout';
import CardLoadingIndicator from '../LoadingIndicator/CardLoadingIndicator';
import ListLoadingIndicator from '../LoadingIndicator/ListLoadingIndicator';
import { BoldLabel, CardGrid, PaddedCard, Tag } from '../Shared/SharedStyles';
import AddFamilyModal from './AddFamilyModal';
import DynamicMessaging from './DynamicMessaging';
import EditFamilyModal from './EditFamilyModal';
import ExtendCoverageModal from './ExtendCoverageModal';
import ManageFamilyActions from './ManageFamilyActions';
import WhoIsEligible from './WhoIsEligible';
import FamilyCard from './FamilyCard';

const Family = () => {
    const {
        language,
        isLoadingFeatures,
        isLoadingMemberData,
        isLoadingFamilyData,
        isLoadingMemberBenefitData,
        member,
        familyMembers,
    } = useShallowEqualSelector((state) => ({
        language: state.Authentication.language,
        isLoadingFeatures: state.Features.isLoading,
        isLoadingMemberData: state.MemberData.isLoadingMemberData,
        isLoadingFamilyData: state.MemberData.isLoadingFamilyData,
        isLoadingMemberBenefitData: state.MemberData.isLoadingMemberBenefitData,
        features: state.Features.data,
        member: state.MemberData.member,
        memberBenefits: state.MemberData.memberBenefits,
        familyMembers: state.MemberData.familyMembers,
    }));
    useAdobeAnalyticsPage('member-profile:family', 'Member Profile - My family');

    const personalTag = useAEMContent(FRAGMENT_MP.common, 'you');
    const dateOfBirthLabel = useAEMContent(FRAGMENT_MP.common, 'dateOfBirthLabel');

    const myFamilyTitle = useAEMContent(FRAGMENT_MP.landing, 'myFamilyTitle');
    const myFamilyHOne = useAEMContent(FRAGMENT_MP.landing, 'myFamilyHOne');

    const isDataLoading = isLoadingMemberBenefitData || isLoadingFamilyData || isLoadingFeatures;

    // Hide terminated dependents (with the exception of students or future terminated dependents)
    const displayableFamilyMembers = useMemo(
        () =>
            familyMembers.filter(
                (familyMember) =>
                    familyMember.benefits.some((benefit) => benefit.eligibleForCoverage) ||
                    (familyMember.familyMemberType === 'SPOUSE' &&
                        familyMember.status === 'TERMINATED' &&
                        new Day(familyMember.terminationDate).isAfter(new Day())) ||
                    familyMember.isStudentStatusExtendable
            ),
        [familyMembers]
    );

    return (
        <Layout pageTitle={myFamilyTitle} id="familyPage">
            <H1>{myFamilyHOne}</H1>
            {isDataLoading && <ListLoadingIndicator data-testid="myFamilyActionListLoadingIndicator" />}
            {!isDataLoading && <DynamicMessaging />}
            <CardGrid>
                {isLoadingMemberData && <CardLoadingIndicator data-testid="memberDataLoadingIndicator" />}
                {!isLoadingMemberData && (
                    <PaddedCard id="memberBasicInformationCard">
                        <Card.Header headerLevel="2">
                            {member.firstName} {member.lastName} <Tag>{personalTag}</Tag>
                        </Card.Header>
                        <Card.Separator />
                        <Card.Content>
                            <BoldLabel noMarginTop>{dateOfBirthLabel}</BoldLabel>
                            {new Day(member.birthDate).format(language, 'D MMM YYYY')}
                        </Card.Content>
                    </PaddedCard>
                )}
                {isLoadingFamilyData && <CardLoadingIndicator data-testid="familyDataLoadingIndicator" />}
                {!isLoadingFamilyData &&
                    displayableFamilyMembers.map((familyMember) => (
                        <FamilyCard familyMember={familyMember} key={familyMember.personId}></FamilyCard>
                    ))}
            </CardGrid>

            {!isLoadingFamilyData && <ManageFamilyActions />}
            <Route path="/gb/member-profile-2/family/addfamily">
                <AddFamilyModal />
            </Route>
            <Route path="/gb/member-profile-2/family/editfamily">
                <EditFamilyModal />
            </Route>
            <Route path="/gb/member-profile-2/family/extendcoverage">
                <ExtendCoverageModal />
            </Route>
            <Route path="/gb/member-profile-2/family/whoiseligible">
                <WhoIsEligible />
            </Route>
        </Layout>
    );
};

export default Family;
